import { api, parseUrl, useApi } from '@gik/core/api';
import UserRegistrationData from '@gik/core/models/gik/auth/UserRegistrationData';
import { BadRequest } from '../HttpErrors';
import { StatusCodes } from 'http-status-codes';
import { ConfigInterface } from 'swr';

// TODO: remove when .net core auth functionality is available
declare const window: LegacyReactWindow;

export interface IUserLoginResponse {
  returnUrl?: string;
  message?: string;
  userId?: string;
}

export interface IUserLoginFormResponse extends IUserLoginResponse {
  rememberMe: boolean;
}

export type UserRegistrationAPIRequest = {
  RegisterEmail: string;
  Firstname: string;
  Lastname: string;
  RegisterPassword: string;
  inkindRouteId?: string;
  subscribeToNewsletter?: boolean;
};

export interface IUserSignUpResponse {
  id?: string;
  returnUrl?: string;
  message?: string;
}

export interface IEmailAddressAlreadyTakenResponse {
  exist: boolean;
  facebook: boolean;
  google: boolean;
}

export async function login(
  emailAddress: string,
  password: string,
  rememberMe: boolean,
  returnUrl?: string
): Promise<IUserLoginResponse> {
  const parseOptions = {
    searchParams: null,
  };

  parseOptions.searchParams = {
    ReturnUrl: returnUrl || window.location.href,
  };

  const finalUrl = parseUrl('authentication/login', parseOptions);

  const response = await api.post(finalUrl, {
    json: {
      LoginEmail: emailAddress,
      LoginPassword: password,
      RememberMe: rememberMe,
    },
  });

  if (!response.ok) {
    switch (response.status) {
      case StatusCodes.BAD_REQUEST:
        throw new BadRequest('Invalid login attempt.');
      default:
        throw response;
    }
  }

  const userLoginResponse: IUserLoginResponse = await response.json();

  return userLoginResponse;
}

export async function logout(): Promise<boolean> {
  try {
    const response = await api.post('authentication/logout');
    if (!response.ok) {
      throw new Error('Failed to log out: ' + response.status + ' ' + response.statusText);
    }

    return true;
  } catch (err) {
    return false;
  }
}

export async function resetPassword(emailAddress: string): Promise<boolean | object> {
  try {
    const response = await api.post(`users/reset-password`, {
      json: {
        emailAddress,
      },
    });

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (err) {
    return false;
  }
}

export async function userSignUp({
  emailAddress,
  firstName,
  lastName,
  password,
  inkindRouteId,
  subscribeToNewsletter,
}: UserRegistrationData): Promise<IUserSignUpResponse> {
  const requestData: UserRegistrationAPIRequest = {
    RegisterEmail: emailAddress,
    Firstname: firstName,
    Lastname: lastName,
    RegisterPassword: password,
    subscribeToNewsletter,
  };

  if (inkindRouteId) {
    requestData.inkindRouteId = inkindRouteId;
  }

  const response = await api.post(`users/register`, {
    json: requestData,
  });

  if (!response.ok) {
    throw (await response.json()) as IUserSignUpResponse;
  }

  return (await response.json()) as IUserSignUpResponse;
}

export async function isEmailAddressAlreadyTaken(email: string): Promise<IEmailAddressAlreadyTakenResponse> {
  const response = await api.get(`users/exist?email=${encodeURIComponent(email)}`);

  if (!response.ok) {
    throw await response.json();
  }

  return (await response.json()) as IEmailAddressAlreadyTakenResponse;
}

export function useEmailAlreadyTaken(email: string, config?: ConfigInterface) {
  return useApi<IEmailAddressAlreadyTakenResponse>(
    email ? `users/exist?email=${encodeURIComponent(email)}` : null,
    config
  );
}
